import { Clock, MapPin, Navigation } from 'lucide-react';
import {
  fmtDist,
  haversineMetres,
  stopColorClass,
  stopIcon,
  stopTypeLabel,
  walkMins,
  type TransportStopFeature,
} from './transportStopUtils';

interface TransportStopListItemProps {
  stop: TransportStopFeature;
  anchor?: { lat: number; lng: number } | null;
  onSelect: (stop: TransportStopFeature) => void;
  onNavigate?: (stop: TransportStopFeature) => void;
  compact?: boolean;
}

export function TransportStopListItem({
  stop,
  anchor,
  onSelect,
  onNavigate,
  compact = false,
}: TransportStopListItemProps) {
  const [lng, lat] = stop.geometry.coordinates;
  const dist = anchor ? haversineMetres(anchor.lat, anchor.lng, lat, lng) : null;
  const mins = dist !== null ? walkMins(dist) : null;
  const Icon = stopIcon(stop.properties?.type);
  const name = stop.properties?.name || 'Unnamed stop';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(stop)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(stop)}
      className={`flex items-center gap-2.5 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:border-emerald-300 dark:hover:border-emerald-600 transition-colors cursor-pointer group ${
        compact ? 'px-2.5 py-2' : 'px-3 py-2.5'
      }`}
    >
      <div
        className={`${compact ? 'w-8 h-8' : 'w-9 h-9'} rounded-xl flex items-center justify-center flex-shrink-0 ${stopColorClass(stop.properties?.type)}`}
      >
        <Icon size={compact ? 14 : 16} />
      </div>

      <div className="min-w-0 flex-1">
        <p className={`font-medium text-gray-900 dark:text-white truncate leading-tight ${compact ? 'text-sm' : 'text-sm'}`}>
          {name}
        </p>
        <div className="flex items-center flex-wrap gap-x-2 mt-0.5">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">{stopTypeLabel(stop.properties?.type)}</span>
          {dist !== null && (
            <>
              <span className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-0.5">
                <MapPin size={9} />
                {fmtDist(dist)}
              </span>
              <span className="text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                <Clock size={9} />
                {mins} min walk
              </span>
            </>
          )}
        </div>
      </div>

      {onNavigate && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(stop);
          }}
          className={`flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors ${
            compact ? 'w-8 h-8 opacity-100' : 'w-8 h-8 opacity-0 group-hover:opacity-100 focus:opacity-100'
          }`}
          title="Get walking directions to this stop"
        >
          <Navigation size={14} />
        </button>
      )}
    </div>
  );
}
